import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorService } from '../../services/vendorService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import type { Vendor, VendorFilters } from '../../types/vendor';

interface VendorListProps {
  onSelectVendor?: (vendor: Vendor) => void;
  onEditVendor?: (vendor: Vendor) => void;
}

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

export const VendorList: React.FC<VendorListProps> = ({
  onSelectVendor,
  onEditVendor
}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<VendorFilters>({
    page: 1,
    limit: 10,
    search: '',
    status: undefined
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendors', filters],
    queryFn: () => vendorService.getVendors(filters)
  });

  const deleteVendorMutation = useMutation({
    mutationFn: vendorService.deleteVendor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    }
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleStatusFilter = (status: VendorFilters['status']) => {
    setFilters(prev => ({ ...prev, status, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDeleteVendor = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      try {
        await deleteVendorMutation.mutateAsync(id);
      } catch (error) {
        console.error('Error deleting vendor:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Vendors</div>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ height: 16, background: 'rgba(241,245,249,1)', borderRadius: 3, width: '25%' }} />
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{ height: 48, background: 'rgba(241,245,249,1)', borderRadius: 3 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 6, padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a' }}>Error loading vendors. Please try again.</p>
      </div>
    );
  }

  const { data: vendors, pagination } = data || { data: [], pagination: null };

  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>

      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Vendors</div>
          {pagination && (
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
              {pagination.total ?? 0} total vendors
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', top: '50%', left: 10, transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <svg width="14" height="14" fill="none" stroke="rgba(217,119,6,0.35)" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search vendors..."
              value={filters.search}
              onChange={handleSearch}
              style={{
                paddingLeft: 30,
                paddingRight: 12,
                paddingTop: 7,
                paddingBottom: 7,
                background: '#faf7ee',
                border: '1px solid rgba(92,78,51,0.25)',
                borderRadius: 4,
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                color: '#5b5a52',
                outline: 'none',
                width: 190,
              }}
              onFocus={e => (e.currentTarget.style.borderColor = 'rgba(217,119,6,0.35)')}
              onBlur={e => (e.currentTarget.style.borderColor = 'rgba(92,78,51,0.25)')}
            />
          </div>

          {/* Status Filter */}
          <select
            value={filters.status || ''}
            onChange={(e) => handleStatusFilter(e.target.value as VendorFilters['status'] || undefined)}
            style={{
              padding: '7px 12px',
              background: '#faf7ee',
              border: '1px solid rgba(92,78,51,0.25)',
              borderRadius: 4,
              fontFamily: "'Inter', sans-serif",
              fontSize: 12,
              color: '#5b5a52',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {(!vendors || vendors.length === 0) ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <svg width="40" height="40" fill="none" stroke="rgba(217,119,6,0.25)" viewBox="0 0 24 24" style={{ margin: '0 auto 12px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>NO VENDORS FOUND</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
                <th style={thStyle}>Vendor</th>
                <th style={thStyle}>Contact</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Outstanding</th>
                <th style={thStyle}>Bills</th>
                <th style={thStyle}>Payment Terms</th>
                <th style={thStyle}>Created</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((vendor: Vendor) => (
                <tr
                  key={vendor.id}
                  style={{ transition: 'background 0.15s', cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  onClick={() => onSelectVendor?.(vendor)}
                >
                  {/* Vendor Name */}
                  <td style={tdStyle}>
                    <div style={{ color: '#1c2033', fontWeight: 500 }}>{vendor.name}</div>
                    {vendor.taxId && (
                      <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>Tax ID: {vendor.taxId}</div>
                    )}
                  </td>

                  {/* Contact */}
                  <td style={tdStyle}>
                    {vendor.email && <div style={{ color: '#5b5a52' }}>{vendor.email}</div>}
                    {vendor.phone && <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{vendor.phone}</div>}
                  </td>

                  {/* Status */}
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: 3,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 8,
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      ...(vendor.status === 'ACTIVE'
                        ? { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' }
                        : { background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' }
                      )
                    }}>
                      {vendor.status}
                    </span>
                  </td>

                  {/* Outstanding Balance */}
                  <td style={tdStyle}>
                    <div style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 15,
                      color: vendor.outstandingBalance > 0 ? '#fbbf24' : '#8a8778'
                    }}>
                      {formatCurrency(vendor.outstandingBalance)}
                    </div>
                  </td>

                  {/* Bills Count */}
                  <td style={tdStyle}>
                    <div style={{ color: '#5b5a52' }}>{vendor._count?.bills || 0}</div>
                  </td>

                  {/* Payment Terms */}
                  <td style={tdStyle}>
                    <div style={{ color: '#5b5a52' }}>
                      {vendor.paymentTerms ? `${vendor.paymentTerms} days` : 'N/A'}
                    </div>
                  </td>

                  {/* Created */}
                  <td style={tdStyle}>
                    <div style={{ fontSize: 11, color: '#6b6a5c' }}>{formatDate(vendor.createdAt)}</div>
                  </td>

                  {/* Actions */}
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditVendor?.(vendor);
                        }}
                        style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5b5a52', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteVendor(vendor.id);
                        }}
                        disabled={deleteVendorMutation.isPending}
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 11,
                          color: '#c95a5a',
                          background: 'none',
                          border: 'none',
                          cursor: deleteVendorMutation.isPending ? 'not-allowed' : 'pointer',
                          padding: 0,
                          opacity: deleteVendorMutation.isPending ? 0.5 : 1,
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
            Showing {(pagination.currentPage - 1) * (filters.limit || 10) + 1}–{Math.min(pagination.currentPage * (filters.limit || 10), pagination.total)} of {pagination.total}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {/* Previous */}
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrev}
              style={{
                padding: '4px 10px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                letterSpacing: '0.1em',
                background: 'rgba(239,232,213,0.5)',
                color: pagination.hasPrev ? '#8a8778' : '#c9c2ab',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 3,
                cursor: pagination.hasPrev ? 'pointer' : 'not-allowed',
                opacity: pagination.hasPrev ? 1 : 0.4,
              }}
            >
              Prev
            </button>

            {/* Page Numbers */}
            {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
              const pageNumber = i + 1;
              const isCurrentPage = pagination.currentPage === pageNumber;
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  style={{
                    padding: '4px 10px',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 9,
                    letterSpacing: '0.1em',
                    background: isCurrentPage ? 'linear-gradient(135deg, #d97706, #b45309)' : 'rgba(239,232,213,0.5)',
                    color: isCurrentPage ? '#1c2033' : '#8a8778',
                    border: isCurrentPage ? 'none' : '1px solid rgba(92,78,51,0.22)',
                    borderRadius: 3,
                    cursor: 'pointer',
                  }}
                >
                  {pageNumber}
                </button>
              );
            })}

            {/* Next */}
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNext}
              style={{
                padding: '4px 10px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                letterSpacing: '0.1em',
                background: 'rgba(239,232,213,0.5)',
                color: pagination.hasNext ? '#8a8778' : '#c9c2ab',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 3,
                cursor: pagination.hasNext ? 'pointer' : 'not-allowed',
                opacity: pagination.hasNext ? 1 : 0.4,
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
